/**
 * TASK-DEATH-003: Smoke Effect Pool Implementation
 *
 * Object pool for smoke particle effects used during death sequences.
 * Uses procedural PIXI.Graphics for smoke particles.
 */

import * as PIXI from 'pixi.js';
import type { AxialCoordinate } from '@drawn-of-war/shared';

/**
 * Pool configuration
 */
export interface SmokeEffectPoolConfig {
  maxSize?: number;
  durationMs?: number;
  particleCount?: number;
}

/**
 * Pool statistics
 */
export interface SmokePoolStatistics {
  totalSpawns: number;
  reuseCount: number;
  recycleCount: number;
}

/**
 * Individual smoke particle
 */
interface SmokeParticle {
  graphics: PIXI.Graphics;
  offsetX: number;
  offsetY: number;
  velocityX: number;
  velocityY: number;
  baseScale: number;
}

/**
 * Smoke effect instance
 */
export interface SmokeEffect {
  container: PIXI.Container;
  particles: SmokeParticle[];
  particleCount: number;
  isActive: boolean;
  progress: number;
  elapsedMs: number;
  durationMs: number;
  startTime: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<SmokeEffectPoolConfig> = {
  maxSize: 10,
  durationMs: 300,
  particleCount: 5
};

/**
 * Smoke visual constants
 */
const SMOKE_COLOR = 0xcccccc; // Light gray
const SMOKE_BASE_RADIUS = 8;
const SMOKE_BLUR_AMOUNT = 4;
const SMOKE_MAX_SCALE = 2.0;
const SMOKE_MIN_SCALE = 0.5;

/**
 * SmokeEffectPool
 *
 * Object pool for smoke effects following game dev best practices.
 */
export class SmokeEffectPool {
  private config: Required<SmokeEffectPoolConfig>;
  private parentContainer: PIXI.Container;
  private hexToPixel: (coord: AxialCoordinate) => { x: number; y: number };
  private pool: SmokeEffect[] = [];
  private active: Set<SmokeEffect> = new Set();
  private stats: SmokePoolStatistics = {
    totalSpawns: 0,
    reuseCount: 0,
    recycleCount: 0
  };

  constructor(
    parentContainer: PIXI.Container,
    hexToPixel: (coord: AxialCoordinate) => { x: number; y: number },
    config: SmokeEffectPoolConfig = {}
  ) {
    this.parentContainer = parentContainer;
    this.hexToPixel = hexToPixel;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Spawn a smoke effect at the given hex position
   */
  public spawn(position: AxialCoordinate): SmokeEffect {
    this.stats.totalSpawns++;

    const pixelPos = this.hexToPixel(position);
    let effect: SmokeEffect;

    // Try to get from pool first
    if (this.pool.length > 0) {
      effect = this.pool.pop()!;
      this.resetEffect(effect, pixelPos);
      this.stats.reuseCount++;
    }
    // Create new if under max size
    else if (this.active.size < this.config.maxSize) {
      effect = this.createEffect(pixelPos);
    }
    // Pool exhausted - recycle oldest active
    else {
      const oldest = this.getOldestActive();
      if (oldest) {
        this.active.delete(oldest);
        this.resetEffect(oldest, pixelPos);
        effect = oldest;
        this.stats.recycleCount++;
      } else {
        // Fallback: create new
        effect = this.createEffect(pixelPos);
      }
    }

    this.active.add(effect);
    this.parentContainer.addChild(effect.container);
    return effect;
  }

  /**
   * Create a new smoke effect
   */
  private createEffect(pixelPos: { x: number; y: number }): SmokeEffect {
    const container = new PIXI.Container();
    container.x = pixelPos.x;
    container.y = pixelPos.y;
    container.alpha = 1;
    container.scale.set(SMOKE_MIN_SCALE);

    const particles: SmokeParticle[] = [];

    // Create smoke particles
    for (let i = 0; i < this.config.particleCount; i++) {
      const particle = this.createParticle();
      particles.push(particle);
      container.addChild(particle.graphics);
    }

    return {
      container,
      particles,
      particleCount: this.config.particleCount,
      isActive: true,
      progress: 0,
      elapsedMs: 0,
      durationMs: this.config.durationMs,
      startTime: performance.now()
    };
  }

  /**
   * Create a single smoke particle
   */
  private createParticle(): SmokeParticle {
    const graphics = new PIXI.Graphics();

    // Draw smoke circle
    graphics.circle(0, 0, SMOKE_BASE_RADIUS);
    graphics.fill({ color: SMOKE_COLOR, alpha: 0.6 });

    // Apply blur
    const blur = new PIXI.BlurFilter();
    blur.blur = SMOKE_BLUR_AMOUNT;
    graphics.filters = [blur];

    // Random offset and velocity for organic look
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * 15;

    return {
      graphics,
      offsetX: Math.cos(angle) * distance,
      offsetY: Math.sin(angle) * distance,
      velocityX: (Math.random() - 0.5) * 0.5,
      velocityY: -Math.random() * 0.3 - 0.1, // Upward drift
      baseScale: 0.8 + Math.random() * 0.4
    };
  }

  /**
   * Reset an effect for reuse
   */
  private resetEffect(effect: SmokeEffect, pixelPos: { x: number; y: number }): void {
    effect.container.x = pixelPos.x;
    effect.container.y = pixelPos.y;
    effect.container.alpha = 1;
    effect.container.scale.set(SMOKE_MIN_SCALE);
    effect.container.visible = true;
    effect.isActive = true;
    effect.progress = 0;
    effect.elapsedMs = 0;
    effect.startTime = performance.now();

    // Reset particles with new random properties
    for (const particle of effect.particles) {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 15;
      particle.offsetX = Math.cos(angle) * distance;
      particle.offsetY = Math.sin(angle) * distance;
      particle.velocityX = (Math.random() - 0.5) * 0.5;
      particle.velocityY = -Math.random() * 0.3 - 0.1;
      particle.baseScale = 0.8 + Math.random() * 0.4;
      particle.graphics.x = particle.offsetX;
      particle.graphics.y = particle.offsetY;
      particle.graphics.alpha = 0.6;
      particle.graphics.scale.set(particle.baseScale);
    }
  }

  /**
   * Update all active effects
   */
  public update(deltaTimeMs: number): void {
    const effectsToComplete: SmokeEffect[] = [];

    for (const effect of this.active) {
      effect.elapsedMs += deltaTimeMs;
      effect.progress = Math.min(1, effect.elapsedMs / effect.durationMs);

      // Easing for smooth animation
      const easeProgress = this.easeOutQuad(effect.progress);

      // Update container scale (expand)
      const scale = SMOKE_MIN_SCALE + (SMOKE_MAX_SCALE - SMOKE_MIN_SCALE) * easeProgress;
      effect.container.scale.set(scale);

      // Update container alpha (fade out)
      effect.container.alpha = 1 - easeProgress;

      // Update individual particles
      for (const particle of effect.particles) {
        // Move particles outward and upward
        particle.graphics.x = particle.offsetX + particle.velocityX * effect.elapsedMs;
        particle.graphics.y = particle.offsetY + particle.velocityY * effect.elapsedMs;

        // Individual particle fade
        particle.graphics.alpha = 0.6 * (1 - easeProgress);
      }

      // Check if complete
      if (effect.progress >= 1) {
        effect.isActive = false;
        effectsToComplete.push(effect);
      }
    }

    // Return completed effects to pool
    for (const effect of effectsToComplete) {
      this.releaseToPool(effect);
    }
  }

  /**
   * Easing function for smooth animation
   */
  private easeOutQuad(t: number): number {
    return 1 - (1 - t) * (1 - t);
  }

  /**
   * Release effect back to pool
   */
  private releaseToPool(effect: SmokeEffect): void {
    this.active.delete(effect);
    effect.container.visible = false;
    this.parentContainer.removeChild(effect.container);
    this.pool.push(effect);
  }

  /**
   * Get oldest active effect for recycling
   */
  private getOldestActive(): SmokeEffect | null {
    if (this.active.size === 0) {
      return null;
    }

    return Array.from(this.active).reduce((oldest, current) =>
      current.startTime < oldest.startTime ? current : oldest
    );
  }

  /**
   * Get active effects (for testing/debugging)
   */
  public getActiveEffects(): SmokeEffect[] {
    return Array.from(this.active);
  }

  /**
   * Get number of active effects
   */
  public getActiveCount(): number {
    return this.active.size;
  }

  /**
   * Get number of pooled effects
   */
  public getPoolSize(): number {
    return this.pool.length;
  }

  /**
   * Get maximum pool size
   */
  public getMaxSize(): number {
    return this.config.maxSize;
  }

  /**
   * Get pool statistics
   */
  public getStatistics(): SmokePoolStatistics {
    return { ...this.stats };
  }

  /**
   * Destroy all effects and cleanup
   */
  public destroy(): void {
    // Destroy all active
    for (const effect of this.active) {
      this.parentContainer.removeChild(effect.container);
      this.destroyEffect(effect);
    }

    // Destroy all pooled
    for (const effect of this.pool) {
      this.destroyEffect(effect);
    }

    this.active.clear();
    this.pool = [];
  }

  /**
   * Destroy a single effect
   */
  private destroyEffect(effect: SmokeEffect): void {
    for (const particle of effect.particles) {
      particle.graphics.destroy();
    }
    effect.container.destroy();
  }
}
